/**
 * Engagement API (work accounts): streaks, badges and weekly goals.
 *
 * Every route is behind the GAMIFICATION feature flag server-side (403 when
 * off, which includes personal accounts), so callers only issue these from
 * surfaces gated by `useEngagementEnabled()`.
 *
 * @module lib/api/engagement.api
 */

import type {
  AchievementWithState,
  AckUnlocksDto,
  CreateGoalDto,
  EngagementGoal,
  EngagementMeResponse,
  EngagementPreferences,
  GoalSuggestion,
  MemberBadgesResponse,
  UpdateEngagementPreferencesDto,
} from '@scrambl/shared'

import { apiClient } from './api-client'
import { createSingletonApi } from './singleton-factory'

class EngagementApi {
  /** Streak, this week, goals, badges and unseen unlocks in one read. */
  async getMe(): Promise<EngagementMeResponse> {
    return apiClient<EngagementMeResponse>('/engagement/me')
  }

  async ackUnlocks(data: AckUnlocksDto): Promise<{ acknowledged: number }> {
    return apiClient<{ acknowledged: number }, AckUnlocksDto>(
      '/engagement/unlocks/ack',
      { method: 'POST', body: data },
    )
  }

  async getAchievements(): Promise<AchievementWithState[]> {
    return apiClient<AchievementWithState[]>('/engagement/achievements')
  }

  async getGoalSuggestions(): Promise<GoalSuggestion[]> {
    return apiClient<GoalSuggestion[]>('/engagement/goals/suggestions')
  }

  async createGoal(data: CreateGoalDto): Promise<EngagementGoal> {
    return apiClient<EngagementGoal, CreateGoalDto>('/engagement/goals', {
      method: 'POST',
      body: data,
    })
  }

  async updateGoal(id: string, target: number): Promise<EngagementGoal> {
    return apiClient<EngagementGoal, { target: number }>(
      `/engagement/goals/${encodeURIComponent(id)}`,
      { method: 'PUT', body: { target } },
    )
  }

  async deleteGoal(id: string): Promise<{ success: true }> {
    return apiClient<{ success: true }>(
      `/engagement/goals/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    )
  }

  async getMemberBadges(memberId: string): Promise<MemberBadgesResponse> {
    return apiClient<MemberBadgesResponse>(
      `/engagement/members/${encodeURIComponent(memberId)}/badges`,
    )
  }

  async updatePreferences(
    data: UpdateEngagementPreferencesDto,
  ): Promise<EngagementPreferences> {
    return apiClient<EngagementPreferences, UpdateEngagementPreferencesDto>(
      '/engagement/preferences',
      { method: 'PUT', body: data },
    )
  }
}

export const engagementApi = createSingletonApi(EngagementApi)
